import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { cfpBaseUrl } from '../../network/urls';
import { resolveScopedTableOfficeCode, resolveTableApiOfficeLevel } from '../../utils/adminCompany';

/** -1 = Descending, 1 = Ascending */
export const DASH_SORT_ORDER_DESC = -1;
export const DASH_SORT_ORDER_ASC = 1;

const initialState = {
    data: [],
    currentPage: 1,
    pageSize: 20,
    dateFrom: '',
    dateTo: '',
    loadFrom: '',
    loadTo: '',
    applicationProgressId: '',
    objectionStatus: '',
    sortOrder: DASH_SORT_ORDER_DESC,
    totalPages: null,
    totalRecords: null,
    isLoading: false,
    error: null,
    // Only the latest fetch may write data (see extraReducers)
    latestRequestId: null,
};

export const fetchPaginatedWithdrawalOfInstallmentOverdue = createAsyncThunk(
    'billingAndCollection/fetchWithdrawalOfInstallmentOverdue',
    async (
        {
            page,
            pageSize,
            dateFrom,
            dateTo,
            applicationProgressId,
            loadFrom,
            loadTo,
            officeCode,
            sortOrder,
        },
        { getState },
    ) => {
        const { auth } = getState();
        const {
            token,
            code,
            hierarchy,
            cfcLevelId,
            roleId,
            userId,
            selectedAdminCompanyCode,
        } = auth;

        const myHeaders = new Headers();
        myHeaders.append('Authorization', token);
        myHeaders.append('Content-Type', 'application/json');

        const effectiveOfficeCode = resolveScopedTableOfficeCode(
            code,
            roleId,
            officeCode,
            selectedAdminCompanyCode,
        );
        const effectiveOfficeLevel = resolveTableApiOfficeLevel({
            userCode: code,
            effectiveOfficeCode,
            hierarchy,
            cfcLevelId,
        });

        const resolvedSortOrder =
                Number(sortOrder) === DASH_SORT_ORDER_ASC ? DASH_SORT_ORDER_ASC : DASH_SORT_ORDER_DESC;
        const payload = {
            pageNumber: String(page || 1),
            pageSize: String(pageSize || 20),
            officeCode: String(effectiveOfficeCode ?? ''),
            officeLevel: String(effectiveOfficeLevel ?? ''),
            overDueFlag: '1',
            dateFrom: dateFrom || '',
            dateTo: dateTo || '',
            loadFrom: loadFrom || '',
            loadTo: loadTo || '',
            applicationProgressId:
                applicationProgressId === 0 ||
                applicationProgressId === '0' ||
                (applicationProgressId !== undefined &&
                    applicationProgressId !== null &&
                    applicationProgressId !== '')
                    ? String(applicationProgressId)
                    : '',
            userId: roleId == 113 || roleId == 1146 ? String(userId ?? '') : '',
            sortOrder: resolvedSortOrder,
        };

        const requestOptions = {
            method: 'POST',
            headers: myHeaders,
            body: JSON.stringify(payload),
            redirect: 'follow',
        };

        const response = await fetch(
            cfpBaseUrl + 'installments/withdrawal/getAllApplications',
            requestOptions,
        );
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || 'Withdrawal overdue applications API issue!');
        }

        const result = await response.json();
        const status = String(result?.status ?? '');

        if (status !== '200' && status !== '400' && status !== '204' && status !== '404') {
            throw new Error(result.message || 'Unable to fetch withdrawal overdue applications!');
        }

        return result;
    },
);

const withdrawalOfInstallmentOverduePaginatedSlice = createSlice({
    name: 'withdrawalOfInstallmentOverduePaginated',
    initialState,
    reducers: {
        nextPage(state) {
            state.currentPage += 1;
        },
        previousPage(state) {
            if (state.currentPage > 1) {
                state.currentPage -= 1;
            }
        },
        filterProgressId(state, action) {
            state.applicationProgressId = action.payload;
        },
        resetProgressIdFilter(state) {
            state.applicationProgressId = '';
        },
        filterObjectionStatus(state, action) {
            state.objectionStatus = action.payload;
        },
        resetObjectionStatusFilter(state) {
            state.objectionStatus = '';
        },
        filterDateFrom(state, action) {
            state.dateFrom = action.payload;
        },
        filterDateTo(state, action) {
            state.dateTo = action.payload;
        },
        resetDateFilter(state) {
            state.dateFrom = '';
            state.dateTo = '';
        },
        filterSortOrder(state, action) {
            const next = Number(action.payload);
            state.sortOrder =
                next === DASH_SORT_ORDER_ASC ? DASH_SORT_ORDER_ASC : DASH_SORT_ORDER_DESC;
        },
        resetSortOrder(state) {
            state.sortOrder = DASH_SORT_ORDER_DESC;
        },
        firstPage(state) {
            state.currentPage = 1;
        },
        lastPage(state, action) {
            state.currentPage = action.payload;
        },
        // Clears every table filter (used when leaving the page). Back to page 1 only if a filter
        // was set, since the page number belonged to the filtered list.
        resetFilters(state) {
            const isSet = (value) => value !== '' && value != null
            const hadFilter = [state.dateFrom, state.dateTo, state.loadFrom, state.loadTo, state.applicationProgressId, state.objectionStatus].some(isSet) ||
                Number(state.sortOrder) === DASH_SORT_ORDER_ASC
            state.dateFrom = ''
            state.dateTo = ''
            state.loadFrom = ''
            state.loadTo = ''
            state.applicationProgressId = ''
            state.objectionStatus = ''
            state.sortOrder = DASH_SORT_ORDER_DESC
            if (hadFilter) state.currentPage = 1
        },

        resetState(state) {
            state.latestRequestId = null
            state.data = [];
            state.currentPage = 1;
            state.isLoading = false;
            state.pageSize = 20;
            state.error = null;
            state.dateFrom = '';
            state.dateTo = '';
            state.loadFrom = '';
            state.loadTo = '';
            state.applicationProgressId = '';
            state.objectionStatus = '';
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchPaginatedWithdrawalOfInstallmentOverdue.pending, (state, action) => {
                state.latestRequestId = action.meta.requestId;
                state.isLoading = true;
            })
            .addCase(fetchPaginatedWithdrawalOfInstallmentOverdue.fulfilled, (state, action) => {
                // An older request finishing late (e.g. one with a filter that has since been
                // changed or cleared) must not overwrite the newer list
                if (action.meta.requestId !== state.latestRequestId) return;
                state.isLoading = false;
                state.error = null;
                const payload = action.payload || {};
                const rows = Array.isArray(payload.data) ? payload.data : [];
                state.data = rows;
                const totalRecords = Number(payload.totalRecords ?? payload.totalCount ?? rows.length);
                state.totalRecords = Number.isFinite(totalRecords) && totalRecords > 0 && rows.length > 0
                    ? totalRecords
                    : rows.length;
                const totalPages = Number(payload.totalPages);
                state.totalPages = Number.isFinite(totalPages) && totalPages > 0 && rows.length > 0
                    ? totalPages
                    : 1;
            })
            .addCase(fetchPaginatedWithdrawalOfInstallmentOverdue.rejected, (state, action) => {
                if (action.meta.requestId !== state.latestRequestId) return;
                state.isLoading = false;
                state.error = action.error.message;
                state.data = [];
                state.totalPages = 1;
                state.totalRecords = 0;
            });
    },
});

export const {
    nextPage,
    previousPage,
    firstPage,
    lastPage,
    filterProgressId,
    filterObjectionStatus,
    filterDateFrom,
    filterDateTo,
    resetProgressIdFilter,
    resetObjectionStatusFilter,
    resetDateFilter,
    resetState,
    resetFilters,
    filterSortOrder,
    resetSortOrder,
} = withdrawalOfInstallmentOverduePaginatedSlice.actions;

export default withdrawalOfInstallmentOverduePaginatedSlice.reducer;
